// Age ticking, regen, slow expiry, phoenix respawn, natural death, and
// pickup drops on enemy kills.
//
// Heritable effects wired in here:
//   - maturationRate trait scales the age tick (faster maturation = faster
//     life, including faster aging into and out of the fertility window).
//   - nursemaid emergent multiplies the age tick of nearby young (pre-fertile)
//     allies by its rateMul.
//   - regen trait restores HP per second to living allies.
//   - phoenix emergent revives an ally once per run on first death.
//   - slow component on enemies ticks down here.

import type { WorldState } from '../world-state';
import { C } from '../components';
import type {
  Age,
  Health,
  FactionComp,
  Position,
  EnemyKindComp,
  Stats,
  Fertility,
  EmergentComp,
  PhoenixComp,
  SlowComp,
  XpComp,
  GenomeComp,
} from '../components';
import { rollPickupDrop } from './pickups';
import { spawnPickup } from '../spawn';
import { ARCHETYPES } from '../enemies';
import { BASE_TRAITS } from '../traits/base';
import type { StatKey } from '../traits/base';
import { ALLELE_MAX } from '../genome';
import type { Genome } from '../genome';

/** XP thresholds per level index (level 1..10). */
const XP_THRESHOLDS = [10, 25, 45, 70, 100, 135, 175, 220, 270, 325] as const;

const NURSEMAID_SCAN_RADIUS = 100;

export function lifecycleSystem(ws: WorldState, dt: number): void {
  const { world } = ws;
  const ages = world.store<Age>(C.age);
  const healths = world.store<Health>(C.health);
  const factions = world.store<FactionComp>(C.faction);
  const positions = world.store<Position>(C.position);
  const kinds = world.store<EnemyKindComp>(C.enemyKind);
  const stats = world.store<Stats>(C.stats);
  const fert = world.store<Fertility>(C.fertility);
  const emergents = world.store<EmergentComp>(C.emergent);
  const phoenixes = world.store<PhoenixComp>(C.phoenix);
  const slows = world.store<SlowComp>(C.slow);
  const xps = world.store<XpComp>(C.xp);
  const genomes = world.store<GenomeComp>(C.genome);
  const pickupRng = ws.rng('pickups');

  // Tick slow expiry on enemies before any speed calc reads it.
  const slowToRemove: number[] = [];
  slows.forEach((s, id) => {
    s.remaining -= dt;
    if (s.remaining <= 0) slowToRemove.push(id);
  });
  for (const id of slowToRemove) slows.delete(id);

  // Age + maturation + nursemaid aura. Allies only; enemies don't age.
  ages.forEach((age, id) => {
    if (factions.get(id)?.side !== 'ally') {
      age.seconds += dt;
      if (age.seconds >= age.lifespan && age.lifespan > 0) {
        // Stale: shouldn't happen for enemies (no age comp), but safe.
      }
      return;
    }
    const s = stats.get(id);
    let tick = dt * (s?.sheet.maturationRate ?? 1);
    // Nursemaid aura: pre-fertile young get a maturation boost if a
    // nursemaid is within range.
    const f = fert.get(id);
    const fracBefore = age.lifespan > 0 ? age.seconds / age.lifespan : 0;
    if (f && fracBefore < f.start) {
      const pos = positions.get(id);
      if (pos) {
        let bestRate = 1;
        ws.allyHash.forEachInRadius(pos.x, pos.y, NURSEMAID_SCAN_RADIUS, (nid) => {
          if (nid === id) return;
          const nm = emergents.get(nid)?.effect.nursemaid;
          if (!nm) return;
          const np = positions.get(nid);
          if (!np) return;
          const d = Math.hypot(np.x - pos.x, np.y - pos.y);
          if (d <= nm.radius) bestRate = Math.max(bestRate, nm.rateMul);
        });
        tick *= bestRate;
      }
    }
    age.seconds += tick;
  });

  // Regen: living allies recover sheet.regen HP per second.
  healths.forEach((h, id) => {
    if (h.current <= 0) return;
    if (factions.get(id)?.side !== 'ally') return;
    const s = stats.get(id);
    if (!s) return;
    const reg = s.sheet.regen;
    if (reg > 0) h.current = Math.min(h.max, h.current + reg * dt);
  });

  // Level-up check: for each ally with accumulated XP, advance levels.
  xps.forEach((xpComp, id) => {
    while (xpComp.level < XP_THRESHOLDS.length) {
      const needed = XP_THRESHOLDS[xpComp.level]!;
      if (xpComp.xp < needed) break;
      xpComp.level++;
      // Heal 15% of max HP on level-up.
      const h = healths.get(id);
      if (h) h.current = Math.min(h.max, h.current + h.max * 0.15);
      // Genome-driven stat boost.
      const s = stats.get(id);
      const g = genomes.get(id)?.genome;
      if (s && g) applyLevelStatBoost(s.sheet, g);
    }
  });

  // Death pass.
  const toRemove: number[] = [];
  ages.forEach((age, id) => {
    if (age.seconds >= age.lifespan) toRemove.push(id);
  });

  healths.forEach((h, id) => {
    if (h.current > 0) return;
    const side = factions.get(id)?.side;
    if (side === 'enemy') {
      ws.killCount++;
      const kind = kinds.get(id)?.kind;
      const guaranteedDrop = kind && ARCHETYPES[kind].guaranteedPickup === true;
      const pos = positions.get(id);
      if (guaranteedDrop || rollPickupDrop(pickupRng)) {
        if (pos) spawnPickup(ws, pos.x, pos.y);
      }
      if (pos) ws.deathEvents.push({ x: pos.x, y: pos.y, faction: 'enemy' });
      toRemove.push(id);
      return;
    }
    if (side === 'ally') {
      // Phoenix: revive once per run instead of dying.
      const ph = phoenixes.get(id);
      if (ph && !ph.used) {
        ph.used = true;
        h.current = h.max; // full HP restoration
        // Reset age slightly so the revived ball gets some life back.
        const age = ages.get(id);
        if (age) age.seconds = Math.max(0, age.seconds - age.lifespan * 0.25);
        return; // don't destroy
      }
      ws.allyDeathCount++;
      const pos = positions.get(id);
      if (pos) ws.deathEvents.push({ x: pos.x, y: pos.y, faction: 'ally' });
    }
    toRemove.push(id);
  });

  for (const id of toRemove) world.destroyEntity(id);
}

/** Returns true iff the ball is within its fertility window. */
export function isFertile(age: Age, fertilityStart: number, fertilityEnd: number): boolean {
  if (age.lifespan <= 0) return false;
  const t = age.seconds / age.lifespan;
  return t >= fertilityStart && t <= fertilityEnd;
}

/**
 * Apply a genome-driven +3% stat boost on level-up.
 * Each stat is boosted by 3% × (allele / ALLELE_MAX), so traits where the
 * ball has strong alleles benefit more than traits where it is weak.
 */
function applyLevelStatBoost(sheet: Record<StatKey, number>, genome: Genome): void {
  for (let i = 0; i < BASE_TRAITS.length; i++) {
    const trait = BASE_TRAITS[i]!;
    const allele = genome[i] ?? 0;
    const relativeStrength = allele / ALLELE_MAX; // 0..1
    if (relativeStrength <= 0) continue;
    sheet[trait.stat] *= 1 + 0.03 * relativeStrength;
  }
}
